import { DEFAULT_MARKER_COLOR } from './mapShared';

/**
 * The clustered-marker icon: a donut whose ring is divided into slices proportional to
 * the marker colours inside the cluster, with the project count in the middle.
 *
 * SW2-2247. Lifted verbatim out of the `MarkerClusterer` renderer in Maps.tsx and made
 * provider-agnostic, because the alternative was two hand-written pie renderers drifting
 * apart. Both layers build their cluster icon from this function, so a cluster looks
 * identical whichever provider is serving the map — which is the whole point of a
 * fallback you can switch back from without users noticing.
 *
 * It returns a detached element rather than markup because that is what both APIs want:
 * Google's `AdvancedMarkerElement` takes `content`, Mapbox's `Marker` takes `element`.
 */

export type ClusterSummary = {
  /** Marker colour -> how many markers in the cluster carry it. */
  colorCounts: Record<string, number>;
  /** Distinct filter-value labels in the cluster, for the tooltip. */
  labels: string[];
  total: number;
};

/**
 * Reduce a cluster's members to the colour proportions and labels the icon needs.
 * `color` and `label` are read off each member; both layers attach them when building
 * their markers.
 */
export const summariseCluster = (
  members: { color?: string; label?: string }[],
): ClusterSummary => {
  const colorCounts: Record<string, number> = {};
  const labelSet = new Set<string>();

  members.forEach(member => {
    const color = member.color || DEFAULT_MARKER_COLOR;
    const label = member.label || 'Unknown';

    colorCounts[color] = (colorCounts[color] || 0) + 1;
    labelSet.add(label);
  });

  return { colorCounts, labels: Array.from(labelSet), total: members.length };
};

/** Monotonic, so each inlined SVG gets its own filter id. See buildClusterSvg. */
let shadowSequence = 0;

/** The donut SVG. Geometry and thresholds are unchanged from the Google renderer. */
export const buildClusterSvg = ({
  colorCounts,
  total,
}: Pick<ClusterSummary, 'colorCounts' | 'total'>): string => {
  const colors = Object.entries(colorCounts);
  const size = Math.min(60, 30 + total * 2);
  const radius = size / 2;
  const cx = radius;
  const cy = radius;
  const r = radius - 4;

  let startAngle = -Math.PI / 2;
  const slices = colors
    .map(([color, count]) => {
      const angle = (count / total) * 2 * Math.PI;
      const endAngle = startAngle + angle;
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cy + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cy + r * Math.sin(endAngle);
      const largeArc = angle > Math.PI ? 1 : 0;
      const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;

      startAngle = endAngle;

      return `<path d="${path}" fill="${color}" />`;
    })
    .join('');

  let svgContent: string;

  // A single-colour cluster skips the slice maths and draws one flat ring.
  if (colors.length === 1) {
    svgContent = `
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="${colors[0][0]}" />
      <circle cx="${cx}" cy="${cy}" r="${r * 0.65}" fill="white" />
      <text x="${cx}" y="${cy}" text-anchor="middle"
            dominant-baseline="central"
            font-size="${total > 99 ? 10 : 12}"
            font-weight="bold" fill="#333">
        ${total}
      </text>`;
  } else {
    svgContent = `
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="white" />
      ${slices}
      <circle cx="${cx}" cy="${cy}" r="${r * 0.65}" fill="white" />
      <text x="${cx}" y="${cy}" text-anchor="middle"
            dominant-baseline="central"
            font-size="${total > 99 ? 10 : 12}"
            font-weight="bold" fill="#333">
        ${total}
      </text>`;
  }

  // Unique per icon. The Google renderer hardcoded id="shadow", so with several
  // clusters on screen every shadow reference resolved to whichever definition came
  // first in the document — harmless in practice because they are identical, but it
  // breaks DOM id uniqueness and couples icons to each other. A counter costs nothing
  // and renders the same.
  const shadowId = `cluster-shadow-${(shadowSequence += 1)}`;

  return `
    <svg width="${size}" height="${size}"
         viewBox="0 0 ${size} ${size}"
         xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="${shadowId}">
          <feDropShadow dx="0" dy="1" stdDeviation="2"
                        flood-opacity="0.3"/>
        </filter>
      </defs>
      <g filter="url(#${shadowId})">
        ${svgContent}
      </g>
    </svg>`;
};

/** The donut as a detached element, ready to hand to either provider's marker API. */
export const buildClusterElement = (summary: ClusterSummary): HTMLDivElement => {
  const div = document.createElement('div');

  div.innerHTML = buildClusterSvg(summary);
  div.style.cursor = 'pointer';
  div.title = summary.labels.join(', ') + ` (${summary.total} projects)`;

  return div;
};
